type Filters = Record<string, unknown>;
type Item = Record<string, unknown> & { id?: unknown; index?: unknown };
type CategoryNode = Item & { relatedItems: CategoryNode[] };

// w URL trzymamy tylko id, pełne obiekty odtwarzamy z list (resolveFilters)
const ID_KEYS = ["terms", "consultants", "departments", "tags", "subtags"];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	!!value && typeof value === "object" && !Array.isArray(value);

const parseValue = (value: string) =>
	/^\d+$/.test(value) ? Number(value) : value;

// tagi zaznaczonego działu (i subtagi zaznaczonego tagu) wynikają z rodzica — nie dublujemy ich w URL
const pruneCategories = (filters: Filters) => {
	if (!Array.isArray(filters.tags)) return filters;
	const departments = (filters.departments || []) as Item[];
	const tags = filters.tags as Item[];
	const subtags = (filters.subtags || []) as Item[];

	return {
		...filters,
		tags: tags.filter((t) => !departments.some((d) => d.index === t.dzialId)),
		subtags: subtags.filter((s) => !tags.some((t) => t.index === s.tagId)),
	};
};

export function filtersToSearch(filters: Filters, defaults: Filters) {
	const params = new URLSearchParams();
	const pruned = pruneCategories(filters);

	for (const [key, defaultValue] of Object.entries(defaults)) {
		const value = pruned[key];
		if (Array.isArray(defaultValue)) {
			const list = (value || []) as unknown[];
			if (list.length) {
				params.set(
					key,
					list.map((v) => (isPlainObject(v) ? v.id : v)).join(","),
				);
			}
		} else if (isPlainObject(defaultValue)) {
			for (const flag of Object.keys(defaultValue)) {
				if ((value as Filters | undefined)?.[flag]) params.set(flag, "1");
			}
		} else if (value != null && value !== "" && value !== defaultValue) {
			params.set(key, String(value));
		}
	}

	return params.toString();
}

export function filtersFromSearch(search: string, defaults: Filters): Filters {
	const params = new URLSearchParams(search);
	const filters: Filters = {};

	for (const [key, defaultValue] of Object.entries(defaults)) {
		if (Array.isArray(defaultValue)) {
			const raw = params.get(key);
			const list = raw ? raw.split(",").map(parseValue) : [];
			filters[key] = ID_KEYS.includes(key) ? list.map((id) => ({ id })) : list;
		} else if (isPlainObject(defaultValue)) {
			filters[key] = Object.fromEntries(
				Object.keys(defaultValue).map((flag) => [
					flag,
					params.get(flag) === "1",
				]),
			);
		} else {
			filters[key] = params.get(key) ?? defaultValue;
		}
	}

	return filters;
}

const strip = ({
	relatedItems,
	dzial_related,
	tagdzial_related,
	...item
}: CategoryNode) => item;

const idsOf = (list: unknown) =>
	new Set(((list || []) as Item[]).map(({ id }) => id));

// rozwija działy/tagi z URL do węzłów drzewa (index, dzialId, tagId, title) — tak jak zaznacza je DefaultFilterList
const resolveCategories = (filters: Filters, tree: CategoryNode[]) => {
	const departmentIds = idsOf(filters.departments);
	const tagIds = idsOf(filters.tags);
	const subtagIds = idsOf(filters.subtags);
	const tagsWithParent = tree.flatMap((d) =>
		d.relatedItems.map((tag) => ({
			tag,
			inDepartment: departmentIds.has(d.id),
		})),
	);
	const isTagSelected = ({ tag, inDepartment }: (typeof tagsWithParent)[0]) =>
		inDepartment || tagIds.has(tag.id);

	return {
		departments: tree.filter((d) => departmentIds.has(d.id)).map(strip),
		tags: tagsWithParent.filter(isTagSelected).map(({ tag }) => strip(tag)),
		subtags: tagsWithParent.flatMap((entry) =>
			entry.tag.relatedItems
				.filter((s) => isTagSelected(entry) || subtagIds.has(s.id))
				.map(strip),
		),
	};
};

const resolveById = (list: unknown, source?: Item[]) =>
	((list || []) as Item[]).map(
		(item) => source?.find(({ id }) => id === item.id) ?? item,
	);

export const hasCategories = (filters: Filters) =>
	["departments", "tags", "subtags"].some(
		(key) => ((filters[key] || []) as unknown[]).length > 0,
	);

export function resolveFilters(
	filters: Filters,
	sources: { tree?: CategoryNode[]; terms?: Item[]; consultants?: Item[] },
): Filters {
	return {
		...filters,
		...(sources.tree && filters.departments
			? resolveCategories(filters, sources.tree)
			: {}),
		...(filters.terms
			? { terms: resolveById(filters.terms, sources.terms) }
			: {}),
		...(filters.consultants
			? { consultants: resolveById(filters.consultants, sources.consultants) }
			: {}),
	};
}
